/* eslint-disable no-nested-ternary */
import { Dialog, DialogPanel, DialogBackdrop } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import ButtonAction from 'src/components/button-action';
import FormInput from 'src/components/form-input';
import { useGlobalContext } from 'src/context/global/use-global-context';
import { authService } from 'src/services/auth.service';
import { handleErrorResponse } from 'src/services/utils';
import { twMerge } from 'tailwind-merge';

interface DialogUserSettingProps {
  onCancel: () => void;
}

const tabs = [
  { key: 'info', name: 'User Info' },
  { key: 'password', name: 'Change Password' },
];
export default function DialogUserSetting({
  onCancel,
}: DialogUserSettingProps) {
  const [currentTab, setCurrentTab] = useState(tabs[0].key);

  return (
    <Dialog
      open
      onClose={onCancel}
      className="relative z-50"
      data-testid="dialog-user-setting"
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            data-testid="dialog-user-setting-panel"
            className={twMerge([
              'relative transform overflow-hidden rounded-lg bg-white px-2 pb-2 pt-3 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95',
              'min-w-96',
            ])}
          >
            <div className="absolute right-2 top-2 flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={onCancel}
                data-testid="dialog-user-setting-close-button"
                className="h-6 w-6 rounded-full p-1 text-gray-500 hover:bg-gray-100"
              >
                <XMarkIcon />
              </button>
            </div>

            <div>
              <div className="mt-2 text-left">
                <div className="relative border-b border-blue-400 pb-0.5">
                  <nav
                    aria-label="Tabs"
                    className="-mb-px flex space-x-8 overflow-x-auto"
                  >
                    {tabs.map((tab) => (
                      <button
                        key={tab.name}
                        type="button"
                        data-testid={`dialog-user-setting-tab-${tab.key}`}
                        aria-current={
                          currentTab === tab.key ? 'page' : undefined
                        }
                        className={twMerge([
                          'text-md whitespace-nowrap border-b-2 px-2 py-1 font-medium',
                          currentTab === tab.key
                            ? 'border-blue-400 bg-blue-400 text-white'
                            : 'border-transparent text-gray-500',
                        ])}
                        onClick={() => setCurrentTab(tab.key)}
                      >
                        {tab.name}
                      </button>
                    ))}
                  </nav>
                </div>
                <div className="mt-4">
                  {currentTab === 'info' && (
                    <div data-testid="dialog-user-setting-info-tab">
                      <UserInfo onCancel={onCancel} />
                    </div>
                  )}
                  {currentTab === 'password' && (
                    <div data-testid="dialog-user-setting-password-tab">
                      <Password onCancel={onCancel} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}

export const UserInfo = ({ onCancel }: { onCancel: () => void }) => {
  const { user, setUser } = useGlobalContext();
  const [loading, setLoading] = useState(false);

  const [values, setValues] = useState({
    name: user?.name,
  });

  useEffect(() => {
    setValues({
      name: user?.name,
    });
  }, [user]);

  const handleSave = async () => {
    if (!values.name) {
      toast.error('Name is required');
      return;
    }

    try {
      setLoading(true);
      const res = await authService.updateUser({
        name: values.name,
      });
      if (res.user) {
        setUser(res.user);
        toast.success('User updated successfully');
        onCancel();
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      toast.error(handleErrorResponse(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <FormInput
        id="user-name"
        name="user-name"
        label="Name"
        placeholder="Name"
        wrapperClassName="w-full"
        data-testid="dialog-user-setting-name-input"
        error={!values.name ? 'Name is required' : ''}
        value={values.name}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setValues({ ...values, name: e.target.value })
        }
      />
      <div className="mt-5 flex w-full justify-end gap-2">
        <ButtonAction
          type="button"
          color="info"
          variant="outlined"
          onClick={onCancel}
          fullWidth
          autoFocus
          data-testid="dialog-user-setting-info-cancel-button"
          className="mt-3 sm:col-start-1 sm:mt-0"
          data-autofocus
        >
          Cancel
        </ButtonAction>
        <ButtonAction
          type="button"
          color="blue"
          variant="contained"
          onClick={handleSave}
          fullWidth
          data-testid="dialog-user-setting-info-save-button"
          className="sm:col-start-2"
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save'}
        </ButtonAction>
      </div>
    </div>
  );
};

export const Password = ({ onCancel }: { onCancel: () => void }) => {
  const [loading, setLoading] = useState(false);
  const { logout } = useGlobalContext();

  const [values, setValues] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleValidate = () => {
    const newErrors = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    };
    if (!values.currentPassword) {
      newErrors.currentPassword = 'Current Password is required';
    }
    if (!values.newPassword) {
      newErrors.newPassword = 'New Password is required';
    }
    if (!values.confirmPassword) {
      newErrors.confirmPassword = 'Confirm Password is required';
    }
    if (values.newPassword !== values.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);

    return Object.values(newErrors).every((error) => error === '');
  };

  const handleSave = async () => {
    if (!handleValidate()) {
      return;
    }

    try {
      setLoading(true);
      await authService.changePassword({
        current_password: values.currentPassword,
        new_password: values.newPassword,
      });
      toast.success('Password updated successfully');
      logout();
      onCancel();
    } catch (error) {
      toast.error(handleErrorResponse(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col gap-2">
        <FormInput
          id="user-password"
          name="user-password"
          label="Password"
          placeholder="Password"
          wrapperClassName="w-full"
          data-testid="dialog-user-setting-current-password-input"
          error={errors.currentPassword}
          value={values.currentPassword}
          type="password"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setValues({ ...values, currentPassword: e.target.value })
          }
        />

        <FormInput
          id="user-new-password"
          name="user-new-password"
          label="New Password"
          placeholder="New Password"
          wrapperClassName="w-full"
          data-testid="dialog-user-setting-new-password-input"
          type="password"
          error={errors.newPassword}
          value={values.newPassword}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setValues({ ...values, newPassword: e.target.value })
          }
        />

        <FormInput
          id="user-confirm-password"
          name="user-confirm-password"
          label="Confirm Password"
          placeholder="Confirm Password"
          wrapperClassName="w-full"
          data-testid="dialog-user-setting-confirm-password-input"
          type="password"
          error={errors.confirmPassword}
          value={values.confirmPassword}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setValues({ ...values, confirmPassword: e.target.value })
          }
        />
      </div>
      <div className="mt-5 flex w-full justify-end gap-2">
        <ButtonAction
          type="button"
          color="info"
          variant="outlined"
          onClick={onCancel}
          fullWidth
          autoFocus
          data-testid="dialog-user-setting-password-cancel-button"
          className="mt-3 sm:col-start-1 sm:mt-0"
          data-autofocus
        >
          Cancel
        </ButtonAction>
        <ButtonAction
          type="button"
          color="blue"
          variant="contained"
          onClick={handleSave}
          fullWidth
          data-testid="dialog-user-setting-password-save-button"
          className="sm:col-start-2"
          disabled={
            loading ||
            !values.currentPassword ||
            !values.newPassword ||
            !values.confirmPassword
          }
        >
          {loading ? 'Saving...' : 'Save'}
        </ButtonAction>
      </div>
    </div>
  );
};
